import { creatorbase, creatorbaseQueries, PaginatedApiResponseData } from '@round/api';
import { useQueryClient } from '@tanstack/react-query';
import { useUpdateProjectMutation } from '@round/creatorbase-helpers/api-hooks';
import useProjectDetails from '../useProjectDetails';
import useInvalidateProjectCampaigns from './useInvalidateProjectCampaigns';
import { getProjectCampaignsListParams } from './projectCampaignsListParams';

export function useReorderProjectCampaigns(platform: creatorbase.Platform) {
    const queryClient = useQueryClient();
    const { project } = useProjectDetails();
    const { update: updateProject } = useUpdateProjectMutation();
    const invalidateProjectCampaigns = useInvalidateProjectCampaigns();

    return async (newOrder: number[]) => {
        if (typeof project?.id !== 'number' || typeof project.campaign_count !== 'number') {
            return;
        }

        const queryKey = creatorbaseQueries.campaigns.keys.list(
            getProjectCampaignsListParams(project.id, platform, project.campaign_count || 1)
        );
        const snapshot = queryClient.getQueryData<PaginatedApiResponseData<creatorbase.Campaign>>(queryKey);

        if (snapshot) {
            const reordered = newOrder
                .map((id) => snapshot.results.find((c) => c.id === id))
                .filter((c): c is creatorbase.Campaign => Boolean(c));
            const results = reordered.length === snapshot.results.length ? reordered : snapshot.results;
            queryClient.setQueryData<PaginatedApiResponseData<creatorbase.Campaign>>(queryKey, {
                ...snapshot,
                results,
            });
        }

        try {
            const updatedProject = await updateProject({
                id: project.id,
                [`${platform}_campaign_ids_order`]: newOrder,
            });
            invalidateProjectCampaigns(platform);
            return { status: 200 as const, data: updatedProject };
        } catch (e) {
            if (!(e instanceof Error && e.name === 'AbortError') && snapshot) {
                queryClient.setQueryData(queryKey, snapshot);
            }
            throw e;
        }
    };
}
